/**
 * Pricing Tiers Service
 *
 * Graduated (declining-block) volume pricing. Each band's lookups bill at that band's own
 * rate, so a month of 10,000 lookups across bands (1–7,000 @ $1.53) and (7,001–14,000 @ $1.27)
 * costs 7,000 x 1.53 + 3,000 x 1.27 — not 10,000 at one rate.
 *
 * Two tables, same shape:
 *   - the DEFAULT table (api/service-pricing-tiers), used by any tier-enabled client who has
 *     no table of their own
 *   - a CLIENT's own table (api/clients/admin/{id}/pricing-tiers), which overrides the default
 *
 * Both are saved as a complete set: a band table only means anything as a contiguous whole.
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface PricingTier {
  id: string;
  clientId: string | null;
  serviceType: string;
  minQuantity: number;
  maxQuantity: number | null;
  ratePerUnit: number;
  /** What a lookup in this band actually bills — ApiRequestLogs.Cost rounds to cents. */
  effectiveRatePerUnit: number;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PricingTierBandItem {
  minQuantity: number;
  maxQuantity: number | null;
  ratePerUnit: number;
  isActive: boolean;
  description?: string | null;
}

// ---- Default tier table ----

export const listDefaultPricingTiers = async (serviceType?: string): Promise<PricingTier[]> => {
  const url = serviceType
    ? `${API_ENDPOINTS.SERVICE_PRICING_TIERS.LIST}?serviceType=${encodeURIComponent(serviceType)}`
    : API_ENDPOINTS.SERVICE_PRICING_TIERS.LIST;
  const response = await makeAuthenticatedRequest(url);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const saveDefaultPricingTiers = async (
  serviceType: string,
  bands: PricingTierBandItem[],
): Promise<PricingTier[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.SERVICE_PRICING_TIERS.SAVE, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serviceType, bands }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- A single client's own tier table ----

export const listClientPricingTiers = async (
  clientId: string,
  serviceType?: string,
): Promise<PricingTier[]> => {
  const base = API_ENDPOINTS.CLIENTS_ADMIN.PRICING_TIERS(clientId);
  const url = serviceType ? `${base}?serviceType=${encodeURIComponent(serviceType)}` : base;
  const response = await makeAuthenticatedRequest(url);
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

export const saveClientPricingTiers = async (
  clientId: string,
  serviceType: string,
  bands: PricingTierBandItem[],
): Promise<PricingTier[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.CLIENTS_ADMIN.PRICING_TIERS(clientId), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ serviceType, bands }),
  });
  if (!response.ok) throw await createApiError(response);
  return response.json();
};

// ---- Shared band-table helpers (used by both editors) ----

export interface BandRow {
  minQuantity: string;
  maxQuantity: string; // '' = unbounded top band
  ratePerUnit: string;
  isActive: boolean;
  description: string;
}

export const toBandRows = (tiers: PricingTier[]): BandRow[] =>
  tiers
    .slice()
    .sort((a, b) => a.minQuantity - b.minQuantity)
    .map((t) => ({
      minQuantity: String(t.minQuantity),
      maxQuantity: t.maxQuantity == null ? '' : String(t.maxQuantity),
      ratePerUnit: String(t.ratePerUnit),
      isActive: t.isActive,
      description: t.description ?? '',
    }));

export const toBandItems = (rows: BandRow[]): PricingTierBandItem[] =>
  rows.map((r) => ({
    minQuantity: parseInt(r.minQuantity, 10),
    maxQuantity: r.maxQuantity.trim() === '' ? null : parseInt(r.maxQuantity, 10),
    ratePerUnit: parseFloat(r.ratePerUnit),
    isActive: r.isActive,
    description: r.description.trim() === '' ? null : r.description.trim(),
  }));

/**
 * Mirrors PricingTierTableValidator on the server so a bad table is caught before a round trip.
 * Kept deliberately in sync — the server is still the authority and re-checks everything.
 *
 * Worth knowing why this is strict: at billing time a lookup that falls in no band is charged
 * at the HIGHEST band rather than failing, so a gap silently overcharges instead of erroring.
 */
export const validateBandRows = (rows: BandRow[]): string | null => {
  for (const r of rows) {
    if (r.minQuantity.trim() === '') return 'Every band needs a "from" quantity.';
    if (!Number.isFinite(parseInt(r.minQuantity, 10))) return `"${r.minQuantity}" is not a valid "from" quantity.`;
    if (r.maxQuantity.trim() !== '' && !Number.isFinite(parseInt(r.maxQuantity, 10)))
      return `"${r.maxQuantity}" is not a valid "to" quantity.`;
    if (r.ratePerUnit.trim() === '') return 'Every band needs a rate.';
    const rate = parseFloat(r.ratePerUnit);
    if (!Number.isFinite(rate) || rate < 0) return `"${r.ratePerUnit}" is not a valid rate.`;
  }

  const active = rows
    .filter((r) => r.isActive)
    .map((r) => ({
      min: parseInt(r.minQuantity, 10),
      max: r.maxQuantity.trim() === '' ? null : parseInt(r.maxQuantity, 10),
    }))
    .sort((a, b) => a.min - b.min);

  if (active.length === 0) return null;

  if (active[0].min !== 1)
    return `The lowest band must start at 1, but starts at ${active[0].min}. Without it, early lookups have no rate.`;

  const unbounded = active.filter((b) => b.max === null);
  if (unbounded.length === 0)
    return 'The highest band must be unbounded — leave its "to" box empty.';
  if (unbounded.length > 1)
    return `Only one band can be unbounded, but ${unbounded.length} are. Every band except the highest needs a "to" value.`;

  for (let i = 0; i < active.length; i++) {
    const b = active[i];
    if (b.max !== null && b.max < b.min) return `The band starting at ${b.min} ends at ${b.max}, before it begins.`;

    if (i === active.length - 1) {
      if (b.max !== null) return 'The unbounded band must be the highest one.';
      break;
    }
    if (b.max === null) return 'The unbounded band must be the highest one.';

    const next = active[i + 1];
    const expected = b.max + 1;
    if (next.min === b.min) return `Two bands both start at ${b.min}.`;
    if (next.min < expected) return `Bands overlap: ${b.min}–${b.max} and ${next.min}–${next.max ?? 'up'} both cover lookup ${next.min}.`;
    if (next.min > expected) return `Gap between bands: nothing covers lookups ${expected}–${next.min - 1}. The next band should start at ${expected}.`;
  }

  return null;
};

/**
 * Graduated total for a month of `quantity` lookups, using the rounded-to-cents rate that
 * actually gets billed. Powers the "what would they pay" preview in both editors.
 */
export const graduatedTotal = (rows: BandRow[], quantity: number): number => {
  const active = rows
    .filter((r) => r.isActive)
    .map((r) => ({
      min: parseInt(r.minQuantity, 10),
      max: r.maxQuantity.trim() === '' ? null : parseInt(r.maxQuantity, 10),
      rate: Math.round(parseFloat(r.ratePerUnit) * 100) / 100,
    }))
    .filter((b) => Number.isFinite(b.min) && Number.isFinite(b.rate))
    .sort((a, b) => a.min - b.min);

  let total = 0;
  for (const band of active) {
    if (quantity < band.min) break;
    const top = band.max == null ? quantity : Math.min(band.max, quantity);
    total += (top - band.min + 1) * band.rate;
  }
  return Math.round(total * 100) / 100;
};

/** True when a rate carries more than 2 decimals, so billing will round it per lookup. */
export const rateRoundsWhenBilled = (raw: string): boolean => {
  const v = parseFloat(raw);
  if (!Number.isFinite(v)) return false;
  return Math.abs(v - Math.round(v * 100) / 100) > Number.EPSILON;
};
